import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { useHubspotDealNameMcpLazyQuery, useHubspotDealsListMcpLazyQuery } from '@anytag/graphql';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  HubspotDealSearchOption,
  AsyncSearchHubspotDealFieldProps,
  SingleAsyncSearchHubspotDealFieldProps,
  MultipleAsyncSearchHubspotDealFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchHubspotDealField<T extends string>(
  props: MultipleAsyncSearchHubspotDealFieldProps<T>
): React.ReactElement;
export function AsyncSearchHubspotDealField<T extends string>(
  props: SingleAsyncSearchHubspotDealFieldProps<T>
): React.ReactElement;
export function AsyncSearchHubspotDealField<T extends string>({
  name,
  help,
  title,
  multiple,
  searchProps,
  ...restProps
}: AsyncSearchHubspotDealFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getHubspotDealsListMcp, loading: fetchingList } = useHubspotDealsListMcpLazyQuery();
  const { getHubspotDealNameMcp, loading: fetchingName } = useHubspotDealNameMcpLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<HubspotDealSearchOption> }>();

  const value = watch(name);
  const selectedHubspotDeals = getValuesArray({ value, multiple }) as string[]; // use string[] internally for comparisons

  const fetchHubspotDeals: AsyncSelectorSearchFieldProps<T, HubspotDealSearchOption>['onSearch'] = async (
    keyword,
    initialLoad
  ) => {
    let hubspotDeals: HubspotDealSearchOption[] = [];

    try {
      if (initialLoad) {
        // TODO: ask BE - should implment like other async search fields
        const results = await Promise.all(
          selectedHubspotDeals.map((id) => getHubspotDealNameMcp({ variables: { hubspotDealId: id } }))
        );
        hubspotDeals = results
          .flatMap((result) => result.data?.hubspotDealNameMcp || [])
          .map((deal) => ({ value: deal.id, label: deal.dealName, ...deal }));
      } else {
        const { data } = await getHubspotDealsListMcp({ variables: { keyword: keyword ?? '' } });
        hubspotDeals =
          data?.hubspotDealsListMcp?.map((deal) => ({ value: deal.id, label: deal.dealName, ...deal })) || [];
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return hubspotDeals;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, HubspotDealSearchOption>,
    'name' | 'loading' | 'onSearch' | 'title' | 'help' | 'searchProps'
  > = {
    name,
    onSearch: fetchHubspotDeals,
    loading: fetchingList || fetchingName,
    title: title ?? t<string>('Selector.Hubspot Deal'),
    help: help ?? t<string>('Tooltip.Hubspot deal update may delay'),
    searchProps: { placeholder: t<string>('Search by Deal name or full Deal ID'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, HubspotDealSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchHubspotDealFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, HubspotDealSearchOption>
      {...(restProps as Partial<SingleAsyncSearchHubspotDealFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
